import { Injectable } from '@angular/core';
import { Subject } from 'rxjs';
import { AuthService } from './auth.service';
import { EnvironmentService } from './environment.service';
import { Websocket2Registration, Websocket2Service } from './websocket2.service';

export type RemoteTaskJobStatus = null | 'success' | 'error';

export class RemoteTaskJobSubTask {
    label: string;
    progress: number;
    status?: RemoteTaskJobStatus;
}

export class RemoteTaskJob {
    id: string;
    user: string;
    label: string;
    status: RemoteTaskJobStatus = null;
    message?: string;
    progress?: number;
    tasks?: Array<RemoteTaskJobSubTask>;
}

export type RemoteTasksChange = {
    action: 'snapshot' | 'upsert' | 'remove';
    job?: RemoteTaskJob;
    id?: string;
};

@Injectable({
    providedIn: 'root'
})
export class RemoteTasksService {

    jobs: Array<RemoteTaskJob> = [];

    jobsChange = new Subject<RemoteTasksChange>();

    private registration: Websocket2Registration = null;

    private _statusSubscription = null;
    private _messageSubscription = null;

    constructor(
        private websocket2Service: Websocket2Service,
        private environmentService: EnvironmentService,
        private authService: AuthService
    ) {
        this._handleAuthStatusChange();

        this.authService.statusChange.subscribe(() => {
            this._handleAuthStatusChange();
        });
    }

    private _disposeRegistration() {
        if(this._statusSubscription != null) {
            this._statusSubscription.unsubscribe();
            this._statusSubscription = null;
        }

        if(this._messageSubscription != null) {
            this._messageSubscription.unsubscribe();
            this._messageSubscription = null;
        }

        if(this.registration != null) {
            this.registration.unregister();
            this.registration = null;
        }

        this.jobs = [];
    }

    private _handleAuthStatusChange() {
        if(this.authService.isLoggedIn() && this.environmentService.environment.WebSocket != null) {
            if(this.registration != null) {
                return;
            }

            this.registration = this.websocket2Service.register('tasks', async () => {
                let _token = await this.authService.getAuthToken();
                return { token: _token };
            });

            if(this.registration == null) {
                return;
            }

            this._statusSubscription = this.registration.statusChange.subscribe((status) => {
                if(status != 'registered') {
                    this.jobs = [];
                }
            });

            this._messageSubscription = this.registration.onMessage.subscribe((data) => {
                this._handleMessage(data);
            });
        }
        else {
            this._disposeRegistration();
        }
    }

    private _handleMessage(data: any) {
        if(data == null || data.action == null) {
            return;
        }

        if(data.action == 'snapshot') {
            this.jobs = Array.isArray(data.jobs) ? data.jobs.slice() : [];
            this.jobsChange.next({ action: 'snapshot' });
            return;
        }

        if(data.action == 'upsert' && data.job != null) {
            let _idx = this.jobs.findIndex((j) => j.id == data.job.id);
            if(_idx == -1) {
                this.jobs.push(data.job);
            }
            else {
                this.jobs[_idx] = data.job;
            }
            this.jobsChange.next({ action: 'upsert', job: data.job });
            return;
        }

        if(data.action == 'remove' && data.id != null) {
            let _idx = this.jobs.findIndex((j) => j.id == data.id);
            if(_idx != -1) {
                this.jobs.splice(_idx, 1);
            }
            this.jobsChange.next({ action: 'remove', id: data.id });
        }
    }

    /** UI helper: remote job/subtask status mapped like local PGTask.status */
    getUiStatus(item: { status?: RemoteTaskJobStatus }): 'waiting' | 'running' | 'success' | 'error' {
        if(item?.status == 'success') return 'success';
        if(item?.status == 'error') return 'error';
        return 'running';
    }

    /** Overall progress: average of subtasks when present, else job.progress. */
    getJobProgress(job: RemoteTaskJob): number {
        if(job?.tasks != null && job.tasks.length > 0) {
            let _sum = 0;
            for(let _t of job.tasks) {
                _sum += _t.progress || 0;
            }
            return _sum / job.tasks.length;
        }
        return job?.progress || 0;
    }

    /** Ask the WS to delete a job owned by the current user. */
    dismiss(id: string) {
        if(this.registration == null || this.registration.status != 'registered') {
            return Promise.reject('offline');
        }

        return this.registration.request({ action: 'dismiss', id: id }).then((data) => {
            // Optimistic local remove; server also broadcasts `remove`
            let _idx = this.jobs.findIndex((j) => j.id == id);
            if(_idx != -1) {
                this.jobs.splice(_idx, 1);
                this.jobsChange.next({ action: 'remove', id: id });
            }
            return data;
        });
    }
}
